import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { imgUrl } from "@/lib/api";
import { Motif } from "@/lib/motifs";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import Turntable from "@/components/product/Turntable";
import { LIFESTYLE_MAP } from "@/data/lifestyle";

// Four sandstone / plaster / jaali / stepwell backdrops for showroom variety
const BACKDROPS = [
  "https://images.unsplash.com/photo-1758413149235-5f764252dbc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxjYXJ2ZWQlMjBzYW5kc3RvbmUlMjBwaWxsYXIlMjBnb2xkZW4lMjBsaWdodHxlbnwwfHx8fDE3ODU0MTExNjl8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1601662528567-526cd06f6582?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHx3YXJtJTIwaXZvcnklMjBwbGFzdGVyJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODU0MTExODF8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1521030283239-29a2fddcbfbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxnZW9tZXRyaWMlMjBzaGFkb3dzfGVufDB8fHx8MTc4NDI0NDk4M3ww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1444505465537-42c1e519902b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxzeW1tZXRyaWNhbCUyMHN0ZXB3ZWxsJTIwbG9uZyUyMHNoYWRvd3N8ZW58MHx8fHwxNzg1NDExMTY5fDA&ixlib=rb-4.1.0&q=85",
];

const bubbleLabelFor = (h) => {
  if (h.key === "fabric") return "Organic Cotton weave";
  if (h.key === "embroidery") return "Hand-embroidered stitches";
  if (h.key === "button") return "Coconut wood buttons";
  if (h.key === "stitch") return "Hand-finished stitching";
  if (h.key === "lining") return "Silk-touch lining";
  return h.label;
};

const bubbleImageFor = (h, imagesByRole, fallback) => {
  if (h.image) return h.image;
  if (imagesByRole[h.key]) return imagesByRole[h.key];
  return fallback;
};

/** Build the turntable frame stack for this product.
 *  Order: lifestyle (kid on plinth) → hero → back / main → sleve.
 *  Deduplicated, always at least 1 frame. Turntable auto-detects tier from length.
 */
const buildFrames = (product) => {
  const imagesByRole = {};
  (product.images || []).forEach((im) => { imagesByRole[im.role] = im.url; });
  const life = LIFESTYLE_MAP[product.slug];
  const backendFrames = Array.isArray(product.turntable_frames) ? product.turntable_frames : [];

  const stack = [
    life,
    imagesByRole.hero,
    imagesByRole.back,
    imagesByRole.main,
    imagesByRole.sleeve || imagesByRole["sleve"],
    ...backendFrames,
  ].filter(Boolean);

  return Array.from(new Set(stack));
};

const ShowroomCard = ({ product, index }) => {
  const [selectedSize, setSelectedSize] = useState((product.sizes || [])[0] || "one-size");
  const { add } = useCart();
  const backdrop = BACKDROPS[index % BACKDROPS.length];
  const sectionRef = useRef(null);
  // First 2 cards mount their turntable eagerly (fold on desktop);
  // rest are deferred until they scroll near the viewport.
  const [mountTurntable, setMountTurntable] = useState(index < 2);

  useEffect(() => {
    if (mountTurntable || !sectionRef.current || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMountTurntable(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px 0px" } // pre-warm 600px before it enters view
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, [mountTurntable]);

  const imagesByRole = {};
  (product.images || []).forEach((im) => { imagesByRole[im.role] = im.url; });

  const frames = useMemo(() => buildFrames(product), [product]);

  // Choose bubbles — if product has hotspots use them, else synthesize from images
  let bubbles = product.hotspots && product.hotspots.length ? product.hotspots.slice(0, 3) : [];
  if (bubbles.length === 0) {
    const candidates = ["embroidery", "fabric", "button", "stitch", "lining"];
    bubbles = candidates
      .filter((k) => imagesByRole[k])
      .slice(0, 3)
      .map((k) => ({ key: k, label: k, image: imagesByRole[k], x: 0, y: 0 }));
  }

  const onAdd = () => {
    if ((product.sizes || []).length > 0 && !selectedSize) return toast.error("Choose a size first");
    add(product, selectedSize || "one-size");
    toast.success(`${product.name} added to your bag`);
  };

  const flip = index % 2 === 1;

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden hairline-b min-h-[480px] md:min-h-[680px]"
      data-testid={`showroom-card-${product.slug}`}
    >
      {/* backdrop */}
      <div className="absolute inset-0">
        <img
          src={backdrop}
          alt=""
          aria-hidden
          loading={index === 0 ? "eager" : "lazy"}
          className="w-full h-full object-cover"
          style={{ filter: "brightness(0.94) saturate(0.85)", opacity: 0.55 }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: flip
              ? "linear-gradient(270deg, rgba(246,241,233,0.94) 0%, rgba(246,241,233,0.55) 26%, rgba(246,241,233,0.05) 50%, rgba(246,241,233,0) 100%)"
              : "linear-gradient(90deg, rgba(246,241,233,0.94) 0%, rgba(246,241,233,0.55) 26%, rgba(246,241,233,0.05) 50%, rgba(246,241,233,0) 100%)",
          }}
        />
        <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: "linear-gradient(180deg, rgba(246,241,233,0) 0%, rgba(246,241,233,0.9) 100%)" }} />
      </div>
      <div className="grain absolute inset-0" />

      <div className={`relative max-w-[1440px] mx-auto px-4 md:px-12 lg:px-24 py-8 md:py-20 grid lg:grid-cols-12 gap-6 md:gap-8 items-center ${flip ? "lg:[direction:rtl]" : ""}`}>
        {/* Info panel */}
        <div className="lg:col-span-4 relative z-10 lg:[direction:ltr]">
          <div className="flex items-center gap-3 mb-4">
            <Motif name={product.motif} size={22} strokeColor="var(--accent)" />
            <span className="nav-item text-ink/60 capitalize">
              {product.category === "women" ? "Women" : product.category === "men" ? "Men" : product.category}
            </span>
          </div>
          <h2 className="serif text-4xl md:text-5xl leading-[1.05]" data-testid={`showroom-name-${product.slug}`}>
            {product.name}
          </h2>
          <p className="mt-4 text-ink/80 text-sm leading-relaxed max-w-[38ch]">{product.hook}</p>
          <div className="mt-6 flex items-baseline gap-4">
            <span className="serif text-3xl" data-testid={`showroom-price-${product.slug}`}>₹{product.price.toLocaleString("en-IN")}</span>
            {product.age_range && <span className="text-xs text-ink/70">· {product.age_range}</span>}
          </div>

          {product.sizes && product.sizes.length > 0 && (
            <div className="mt-6" data-testid={`showroom-sizes-${product.slug}`}>
              <div className="nav-item mb-2 text-ink/60">Size</div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSelectedSize(s)}
                    className={`px-3 py-1.5 hairline text-xs transition-colors ${selectedSize === s ? "bg-ink text-ground border-ink" : "hover:border-ink"}`}
                    data-testid={`showroom-size-${product.slug}-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={onAdd} className="btn-solid" data-testid={`showroom-add-${product.slug}`}>Add to bag</button>
            <Link to={`/product/${product.slug}`} className="crimson-link nav-item !text-[12px]" data-testid={`showroom-see-${product.slug}`}>
              See the piece →
            </Link>
          </div>
        </div>

        {/* Center — REAL canvas turntable on a plinth */}
        <div className="lg:col-span-5 relative flex flex-col items-center justify-center lg:[direction:ltr]">
          <div
            className="relative w-full max-w-[440px] mx-auto aspect-[4/5]"
            style={{
              background: "radial-gradient(ellipse at 50% 60%, rgba(232,223,209,0.9) 0%, rgba(246,241,233,0.5) 60%, rgba(246,241,233,0) 100%)",
            }}
            data-testid={`showroom-turntable-wrap-${product.slug}`}
          >
            {mountTurntable ? (
              <Turntable frames={frames} alt={product.name} />
            ) : (
              // Lightweight placeholder — just the hero frame until scrolled near
              <img
                src={imgUrl(frames[0])}
                alt={product.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-contain"
              />
            )}
          </div>

          {/* wooden plinth ellipse */}
          <div className="mx-auto -mt-2 w-[62%]" style={{
            height: 22,
            background: "radial-gradient(ellipse at center, rgba(74,54,40,0.55) 0%, rgba(74,54,40,0.15) 55%, rgba(74,54,40,0) 100%)",
            borderRadius: "50%",
            filter: "blur(2px)",
          }} />
          <div className="mx-auto -mt-2 hairline-t w-[48%]" style={{ borderColor: "rgba(74,54,40,0.35)" }} />

          {/* 360° drag hint */}
          <div className="mt-4 flex items-center gap-3 text-xs text-ink/60" data-testid={`showroom-360-${product.slug}`}>
            <span className="inline-block w-7 h-7 rounded-full hairline flex items-center justify-center animate-melavi-breathe">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.2">
                <path d="M4 12 a 8 6 0 0 0 16 0 a 8 6 0 0 0 -16 0" />
                <path d="M4 12 L 2 10 M4 12 L 2 14" strokeLinecap="round" />
              </svg>
            </span>
            <span>360° · drag to turn · {frames.length} views</span>
          </div>
        </div>

        {/* Right — floating detail bubbles */}
        <div className="lg:col-span-3 relative lg:[direction:ltr] hidden lg:block">
          <div className="flex flex-col gap-5">
            {bubbles.map((b, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`showroom-bubble-${product.slug}-${b.key || i}`}>
                <div
                  className="w-20 h-20 rounded-full overflow-hidden flex-none"
                  style={{ border: "1px solid var(--stone)", background: "var(--sand)" }}
                >
                  <img src={imgUrl(bubbleImageFor(b, imagesByRole, frames[1] || frames[0]))} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                <span className="text-xs leading-tight text-ink/80 max-w-[14ch]">{bubbleLabelFor(b)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile bubble row */}
        <div className="lg:hidden col-span-full mt-4">
          <div className="flex gap-3 overflow-x-auto no-scrollbar">
            {bubbles.map((b, i) => (
              <div key={i} className="flex-none flex flex-col items-center gap-2 w-20">
                <div className="w-14 h-14 rounded-full overflow-hidden" style={{ border: "1px solid var(--stone)" }}>
                  <img src={imgUrl(bubbleImageFor(b, imagesByRole, frames[1] || frames[0]))} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                <span className="text-[10px] text-center text-ink/70 leading-tight">{bubbleLabelFor(b)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShowroomCard;
